/* ── vorm ───────────────────────────────────────────────────────
   Port van greenvision-ems/web/src/data/vorm.js. Dit is het koppelvlak tussen
   de data en de tekening: de antwoorden van de drie dataroutes gaan erin, de
   dagenreeks waarop Dashboard tekent komt eruit.

   Eén bewuste afwijking van de bron: bouwHistorie krijgt de tijdzone van de
   locatie als vijfde argument. In de bron zat die als constante in tijd.js; hier
   komt hij uit ems.locatie.tijdzone. De vorm van de invoer (de rijen van
   /api/assets, /api/kwartieren en /api/dagtotalen) en van de uitvoer is
   ongewijzigd.

   Wat uit Zympler komt staat in dezelfde kolommen als in het oude portaal. Wat
   een dag níet kreeg is in de totalen null en niet 0 (zie ONBEKEND_ALS_LEEG):
   een nul is een meting, een ontbrekende verdeling is geen verdeling van nul. */

import { Q, lokaal, sleutelNaarDatum, eisZone } from './tijd';

export { Q };

const NUL = () => new Float32Array(Q);

const ZYMPLER_KOL = [
  'load', 'chg', 'chg1', 'chg2', 'bat', 'bat1', 'bat2', 'soc', 'soc1', 'soc2',
  'net', 'netRuw', 'zonV', 'batV', 'netV', 'dirC', 'dirR',
  'zonLoad', 'zonChg', 'zonBat', 'zonNet', 'batLoad', 'batChg', 'netLoad', 'netChg', 'netBat',
  'socGroen', 'socGrijs', 'batChgGroen', 'batChgGrijs', 'dirG',
  'planNet', 'planSoc', 'curt', 'vsoc1', 'vsoc2', 'flexUit', 'flexIn',
  /* de zon van de omvormers (Solis) naast de leidende zon, en batterij naar net */
  'pvSolis', 'batNet',
];

const ZYMPLER_TOT = [
  'load', 'chg', 'chg1', 'chg2', 'netIn', 'exp', 'ontladen', 'geladen',
  'zonV', 'batV', 'netV', 'dirC', 'dirR', 'piek', 'piekRuw', 'socGem', 'boven',
  'zonLoad', 'zonChg', 'zonBat', 'zonNet', 'batLoad', 'batChg', 'netLoad', 'netChg', 'netBat',
  'dirG', 'batChgGroen', 'batChgGrijs', 'curt', 'groenEind', 'grijsEind', 'planAfw',
  'pvSolis', 'batNet',
];

/* Uit de balans van de locatie (Zympler, /telemetry/locations; zie
   ingest/lib/map.ts). Het teken en de bestemming staan in de naam: zon_naar_pand
   is de zon die op de locatie verbruikt werd. "onbekend" is bij Zympler verbruik
   dat niet aan een apart gemeten asset toe te wijzen is — dat is verbruik op de
   locatie, dus pand. */
const VERDEEL_KOL: Readonly<Record<string, string>> = Object.freeze({
  zon_naar_pand: 'zonLoad', zon_naar_onbekend: 'zonLoad', zon_naar_lader: 'zonChg',
  zon_naar_bat: 'zonBat', zon_naar_net: 'zonNet',
  bat_naar_pand: 'batLoad', bat_naar_onbekend: 'batLoad', bat_naar_lader: 'batChg', bat_naar_net: 'batNet',
  net_naar_pand: 'netLoad', net_naar_lader: 'netChg', net_naar_bat: 'netBat',
});
export const VERDEEL_VELDEN = Object.freeze(Object.keys(VERDEEL_KOL));

/* Totalen die alleen bestaan als de bron ze leverde. Zonder is het null — "niet
   beschikbaar" — en niet 0. Gemeten 18-09-2026: het scherm zei "zelfvoorziening
   100%, 351 van 0 kWh" omdat verbruik 0 was waar het onbekend was. */
const VERDEEL_TOT = ['zonLoad', 'zonChg', 'zonBat', 'zonNet', 'batLoad', 'batChg', 'batNet',
  'netLoad', 'netChg', 'netBat', 'zonV', 'batV', 'netV'];
/* Niet geleverd en als getal op het scherm, waar een 0 een meting zou lijken.
   (Wat alleen in de grafieken meetelt, zoals chg1/chg2, blijft 0: daar is een
   lege laag geen bewering.) */
const ONBEKEND_ALS_LEEG = ['planAfw'];

function legeKol(): Record<string, any> {
  const kol: Record<string, any> = { pv: NUL() };
  for (const naam of ZYMPLER_KOL) kol[naam] = NUL();
  kol.st1 = new Uint8Array(Q);
  kol.st2 = new Uint8Array(Q);
  kol.bron = new Uint8Array(Q);
  return kol;
}

function legeTot(): Record<string, number> {
  const tot: Record<string, number> = { pv: 0, pvBerekend: 0, gaten: 0 };
  for (const naam of ZYMPLER_TOT) tot[naam] = 0;
  return tot;
}

/**
 * Bouwt de dagenreeks waar de weergavecomponenten op draaien.
 *
 * @param assets      omvormers uit haalAssets, op volgorde
 * @param kwartieren  rijen uit haalKwartieren
 * @param dagtotalen  rijen uit haalDagtotalen
 * @param dagen       lijst dagsleutels, oplopend; de laatste is de referentiedag
 * @param zone        ems.locatie.tijdzone — verplicht
 */
/**
 * Eén meting die niet de zon is, op zijn plek in de tekening.
 *
 * De tekenconventie van het dashboard, en die is niet vrijblijvend:
 *   bat  positief = ontladen, negatief = laden  (Dashboard leest -bat als "laden")
 *   net  positief = afname,   negatief = teruglevering (-net is "export")
 *   soc  procenten, 0-100
 *
 * Wat hier binnenkomt is per richting gescheiden en altijd positief
 * (bat_laden, bat_ontladen, net_import, net_export), want zo levert Zympler het
 * en zo staat het in ems.meting. Het teken wordt hier gezet, op één plek.
 *
 * @param kast  0 of 1 voor de eerste twee batterijkasten, anders undefined
 * @param gun   0 of 1 voor de eerste twee laadpunten (gunkolom chg1/chg2), anders undefined
 */
function vulZympler(d: any, veld: string, waarde: number, i: number, kast: number | undefined, gun?: number): void {
  const kolom = (naam: string, v: number) => { d.kol[naam][i] += v; };
  /* Het laadvermogen per laadpunt (ADR 0039): uit de meterstanden van afgeronde
     laadsessies, als kwartiergemiddelde. Een kwartier zonder rij is geen meting:
     een sessie die nog loopt staat er nog niet in. */
  if (veld === 'laad_vermogen') {
    d.gemeten.lader[i] = 1;
    kolom('chg', waarde);
    if (gun === 0) kolom('chg1', waarde); else if (gun === 1) kolom('chg2', waarde);
    return;
  }
  if (veld === 'bat_ontladen' || veld === 'bat_laden') d.gemeten.bat[i] = 1;
  else if (veld === 'net_import' || veld === 'net_export') d.gemeten.net[i] = 1;
  else if (veld === 'bat_soc') d.gemeten.soc[i] = 1;
  /* De verdeling: vermogen per kwartier, en de dagtotalen als vermogen/4 — de
     _kwh-varianten worden niet opgehaald; dat zou de hoeveelheid rijen per dag
     verdubbelen voor een verschil dat alleen in het lopende kwartier bestaat. */
  if (VERDEEL_KOL[veld]) {
    d.gemeten.verdeling[i] = 1;
    kolom(VERDEEL_KOL[veld], waarde);
    return;
  }
  switch (veld) {
    /* De zon van de hele locatie, uit dezelfde aanroep als batterij en net. Hij
       wordt na de lus de leidende zon (zie zonUitZympler); tot dan staat hij los. */
    case 'zon_locatie': d.gemeten.zon[i] = 1; d._zonZ[i] += waarde; break;
    case 'zon_locatie_kwh': d._zonZkwh[i] += waarde; break;
    case 'verbruik': d.gemeten.verbruik[i] = 1; kolom('load', waarde); break;
    case 'verbruik_kwh': d._verbruikKwh += waarde; break;
    /* Een stand in kWh, zoals de groen/grijs-grafiek hem tekent. */
    case 'bat_soc_groen': d.kol.socGroen[i] = waarde; d.gemeten.socHerkomst[i] = 1; break;
    case 'bat_soc_grijs': d.kol.socGrijs[i] = waarde; d.gemeten.socHerkomst[i] = 1; break;
    case 'bat_ontladen':
      kolom('bat', waarde);
      if (kast === 0) kolom('bat1', waarde); else if (kast === 1) kolom('bat2', waarde);
      break;
    case 'bat_laden':
      kolom('bat', -waarde);
      if (kast === 0) kolom('bat1', -waarde); else if (kast === 1) kolom('bat2', -waarde);
      break;
    case 'bat_soc':
      d._socSom[i] += waarde;
      d._socN[i] += 1;
      if (kast === 0) d.kol.soc1[i] = waarde; else if (kast === 1) d.kol.soc2[i] = waarde;
      break;
    /* netRuw niet hier: dat is het net zónder batterij, en dat kan pas als alle
       metingen van het kwartier binnen zijn. Zie de piek in bouwHistorie. */
    case 'net_import': kolom('net', waarde); break;
    case 'net_export': kolom('net', -waarde); break;
    /* De dagtotalen komen uit de energie en niet uit de som van de vermogens:
       het lopende kwartier is korter dan een kwartier, en dan klopt waarde/4 niet. */
    case 'net_import_kwh': d.tot.netIn += waarde; break;
    case 'net_export_kwh': d.tot.exp += waarde; break;
    case 'bat_ontladen_kwh': d.tot.ontladen += waarde; break;
    case 'bat_laden_kwh': d.tot.geladen += waarde; break;
    /* bat_energie (kWh in de kast) heeft nog geen plek in de tekening. Bewust
       niets doen is beter dan hem in een kolom duwen die iets anders betekent. */
    default: break;
  }
}

export function bouwHistorie(assets: any[], kwartieren: any[], dagtotalen: any[], dagen: string[], zone: string): any[] {
  eisZone(zone);
  const omvormers = assets.filter((a) => a.type === 'omvormer');
  const perId = new Map(omvormers.map((a, i) => [a.id, i]));

  /* De batterijen op volgorde, zodat kast 1 in soc1/bat1 landt en kast 2 in
     soc2/bat2. Meer dan twee kasten kan het model niet tonen; die tellen wel mee
     in de optelling (bat, soc) maar krijgen geen eigen kolom. */
  const batterijen = assets
    .filter((a) => a.type === 'batterij')
    .sort((x, y) => (Number(x.volgorde) || 0) - (Number(y.volgorde) || 0));
  const batIndex = new Map(batterijen.map((a, i) => [a.id, i]));
  /* Hetzelfde voor de laadpunten: het eerste laadpunt in volgorde vult chg1,
     het tweede chg2 (de gunkolommen, ADR 0027). Meer laadpunten tellen mee in
     chg maar krijgen geen eigen kolom. */
  const gunIndex = new Map(assets
    .filter((a) => a.type === 'gun')
    .sort((x, y) => (Number(x.volgorde) || 0) - (Number(y.volgorde) || 0))
    .map((a, i) => [a.id, i]));

  const index = new Map<string, any>();
  const rij = dagen.map((sleutel, k) => {
    const datum = sleutelNaarDatum(sleutel);
    const dow = datum.getDay();
    const d: any = {
      k, sleutel, datum, dow, werk: dow >= 1 && dow <= 5,
      kol: legeKol(),
      tot: legeTot(),
      /* per omvormer: kwartieren voor de tekening, dagtotaal voor de controle */
      omvormers: omvormers.map((a) => ({
        id: a.id, naam: a.naam, nominaalKw: Number(a.nominaal_kw) || null,
        externId: a.extern_id || null, datalogger: a.datalogger_sn || null,
        /* kol houdt de waarde vast, heeft of er überhaupt een rij was. Zonder
           dat tweede is een omvormer die nul levert niet te onderscheiden van
           een datalogger die zweeg, en dat verschil is het hele punt. */
        kol: NUL(), heeft: new Uint8Array(Q),
        kwh: 0, kwhBerekend: 0, kwartieren: 0, laatste: null,
      })),
      metingen: 0,
      /* Een laadstand is een niveau, geen som: bij twee kasten is het gemiddelde
         de waarheid en niet het totaal. Som en telling worden hier verzameld en
         na de lus tot een gemiddelde gemaakt; daarna weg. */
      _socSom: NUL(), _socN: new Uint8Array(Q),
      /* Per kwartier: kwam er voor dit gegeven iets binnen? Dezelfde rol als
         heeft[i] bij de omvormers. Zonder dit is een batterij die op 0 kW staat
         niet te onderscheiden van een kwartier waarin niemand iets doorgaf, en
         de kolommen zijn nul-gevuld — dus "geen meting" tekent als "0,0 kW, 0%".
         Gemeten op 18-09 om 08:15 bij de eerste locatie met Zympler: de
         ophaaldienst draaide nog niet, en het scherm meldde een lege batterij
         en nul afname. (Geen klantnaam hier: dit is gedeelde dashboardcode,
         ADR 0010, en tests/ems/klantnamen bewaakt dat.) */
      gemeten: {
        bat: new Uint8Array(Q), net: new Uint8Array(Q), soc: new Uint8Array(Q),
        /* de balans van de locatie: zon (leidend), verbruik, verdeling, groen/grijs */
        zon: new Uint8Array(Q), verbruik: new Uint8Array(Q), verdeling: new Uint8Array(Q), socHerkomst: new Uint8Array(Q),
        /* laadvermogen van de laadpunten (laad_vermogen, ADR 0039) */
        lader: new Uint8Array(Q),
      },
      _zonZ: NUL(), _zonZkwh: NUL(), _verbruikKwh: 0,
    };
    index.set(sleutel, d);
    return d;
  });

  /* kwartieren over de dagen verdelen */
  for (const m of kwartieren) {
    const { dag, i } = lokaal(m.tijdstip, zone);
    const d = index.get(dag);
    if (!d) continue;
    /* Een rij zonder getal is geen meting van nul. Als die als 0 meetelde, zette
       hij heeft/gemeten op 1 en tekende het scherm "0,0 kW" waar niets bekend was. */
    if (m.waarde === null || m.waarde === undefined || m.waarde === '') continue;
    const kw = Number(m.waarde);
    if (!Number.isFinite(kw)) continue;

    /* Alles wat niet de zon is gaat langs een andere weg. Let op wat hieronder
       NIET gebeurt: d.metingen telt uitsluitend zonmetingen. Daar hangt
       heeftData aan, en daarmee de hele storingsanalyse van de omvormers
       (service.ts). Een dag met alleen batterijdata is voor die analyse geen
       dag met data, en dat moet zo blijven. */
    if (m.veld && m.veld !== 'pv_vermogen') {
      vulZympler(d, m.veld, kw, i, batIndex.get(m.asset_id), gunIndex.get(m.asset_id));
      continue;
    }

    const o = perId.get(m.asset_id);
    d.kol.pv[i] += kw;
    d.metingen++;
    /* Het totaal komt uit de rijen zelf en niet uit de 96 vakjes. Op de nacht
       waarin de klok terugloopt zijn er honderd kwartieren; die extra vallen in
       een al gevuld vakje, maar mogen niet uit het dagtotaal verdwijnen. */
    d.tot.pvBerekend += kw / 4;
    if (o !== undefined) {
      const ov = d.omvormers[o];
      ov.kol[i] += kw;
      ov.heeft[i] = 1;
      ov.kwhBerekend += kw / 4;
      ov.kwartieren++;
      if (ov.laatste === null || m.tijdstip > ov.laatste) ov.laatste = m.tijdstip;
    }
  }

  /* dagtotalen: dit is de waarheid voor de controle */
  const gezien = new Set<string>();
  for (const t of dagtotalen) {
    const d = index.get(String(t.dag).slice(0, 10));
    if (!d) continue;
    const kwh = Number(t.kwh) || 0;
    const o = perId.get(t.asset_id);
    if (o !== undefined) d.omvormers[o].kwh = kwh;
    gezien.add(d.sleutel);
    d.tot.pv += kwh;
  }
  /* De laadstand van de locatie is het gemiddelde van de kasten die op dat
     kwartier iets zeiden, niet hun som. */
  for (const d of rij) {
    for (let i = 0; i < Q; i++) if (d._socN[i]) d.kol.soc[i] = d._socSom[i] / d._socN[i];
    delete d._socSom;
    delete d._socN;

    /* Is de VERDELING er? Dat is iets anders dan "is Zympler een bron".
     *
     * zonV, batV en netV zeggen hoeveel van elke bron daadwerkelijk in de vraag
     * verdween. Dat is een berekening die de vraagkant en de stroommatrix nodig
     * heeft, en die halen we (nog) niet op: de kolommen bestaan maar blijven
     * leeg. De analysegrafiek koos tot nu toe op de bron — met Zympler aan las
     * hij zonV in plaats van pv, en verdween de zon uit beeld op het moment dat
     * de batterij binnenkwam. Hij hoort te kiezen op dít. */
    d.heeftVerdeling = d.gemeten.verdeling.some((v: number) => v === 1);
    if (d.heeftVerdeling) {
      /* zonV/batV/netV: wat van elke bron in het verbruik verdween, pand plus
         lader. Samen zijn ze het verbruik; zon naar batterij of net en laden van
         de batterij staan in de grafiek aan de andere kant van de nul. */
      for (let i = 0; i < Q; i++) {
        d.kol.zonV[i] = d.kol.zonLoad[i] + d.kol.zonChg[i];
        d.kol.batV[i] = d.kol.batLoad[i] + d.kol.batChg[i];
        d.kol.netV[i] = d.kol.netLoad[i] + d.kol.netChg[i];
      }
    }

    /* De piek van het net, en wat die zonder batterij was geweest.
     *
     * Het oude portaal rekende ze uit in zijn simulatie: piek = max(net),
     * piekRuw = max(net zonder batterij). Bij de port bleven ze op nul staan, en
     * netRuw werd een kopie van net. Gemeten op 18-09-2026 om 11:50: de kaart
     * meldde "piek vandaag 0 kW" terwijl er 88 kW werd afgenomen, en "ongestuurd"
     * stond gelijk aan de piek — het effect van de batterij was onzichtbaar.
     *
     * Zonder batterij had het net geleverd wat de batterij nu leverde, en niet
     * geleverd wat ze nu laadde: netRuw = net + bat (bat positief = ontladen).
     * Alleen kwartieren met een netmeting tellen: een kwartier zonder meting is
     * geen kwartier zonder afname. Ontbreekt op zo'n kwartier de batterij, dan
     * staat daar 0 in bat en is het gemeten net het beste wat er is. */
    let piek = 0, piekRuw = 0;
    for (let i = 0; i < Q; i++) {
      if (!d.gemeten.net[i]) continue;
      d.kol.netRuw[i] = d.kol.net[i] + d.kol.bat[i];
      if (d.kol.net[i] > piek) piek = d.kol.net[i];
      if (d.kol.netRuw[i] > piekRuw) piekRuw = d.kol.netRuw[i];
    }
    d.tot.piek = piek;
    d.tot.piekRuw = piekRuw;
  }

  /* geen dagtotaal, dan telt wat er aan kwartieren ligt */
  for (const d of rij) {
    if (!gezien.has(d.sleutel)) {
      d.tot.pv = d.tot.pvBerekend;
      for (const ov of d.omvormers) ov.kwh = ov.kwhBerekend;
    }
    zonUitZympler(d);
    totalenVanDeBalans(d);
    d.tot.piekPv = d.kol.pv.reduce((a: number, b: number) => Math.max(a, b), 0);
    d.heeftData = d.metingen > 0;
    /* Een dag met een meting van welke bron dan ook. heeftData hierboven telt
       alleen de omvormers — daar hangt de storingsanalyse van service.ts aan —
       maar het scherm opent op de laatste dag waarop íets binnenkwam: na
       middernacht is dat vandaag, ook als de zon nog onder is. */
    d.heeftMeting = d.heeftData || ['bat', 'net', 'soc', 'zon', 'verbruik', 'verdeling', 'lader']
      .some((k) => d.gemeten[k].some((v: number) => v === 1));
    /* Het productievenster van de dag: het eerste tot en met het laatste
       kwartier waarin welke omvormer dan ook een rij had. Daarbinnen is een
       ontbrekende rij een gat, daarbuiten is het nacht. */
    let van = -1, tot = -1;
    for (const ov of d.omvormers) {
      for (let i = 0; i < Q; i++) {
        if (!ov.heeft[i]) continue;
        if (van < 0 || i < van) van = i;
        if (i > tot) tot = i;
      }
    }
    d.venster = van < 0 ? null : { van, tot };
    delete d._zonZ;
    delete d._zonZkwh;
    delete d._verbruikKwh;
  }

  return rij;
}

/**
 * Zympler is leidend voor de zon (Stan, 19-09-2026).
 *
 * De zon uit de locatiereeks komt uit dezelfde aanroep, met hetzelfde tijdstip,
 * als batterij, net en de verdeling — en telt daarmee op. Solis loopt apart, om
 * de tien minuten, en viel midden op de dag geregeld weg. Waar Zympler een zon
 * heeft wordt die kol.pv; de omvormers blijven Solis (per stuk, voor de
 * plattegrond en de storingsanalyse) en hun som blijft staan als pvSolis.
 *
 * Een kwartier zonder Zympler-zon houdt de Solis-waarde, zodat een dag van vóór
 * de koppeling hetzelfde blijft tonen als voorheen.
 */
function zonUitZympler(d: any): void {
  d.kol.pvSolis.set(d.kol.pv);
  d.tot.pvSolis = d.tot.pv;
  let zympler = false, restSolis = 0, zonKwh = 0;
  for (let i = 0; i < Q; i++) {
    if (d.gemeten.zon[i]) {
      zympler = true;
      d.kol.pv[i] = d._zonZ[i];
      zonKwh += d._zonZkwh[i] || d._zonZ[i] / 4;
    } else {
      restSolis += d.kol.pvSolis[i] / 4;
    }
  }
  if (zympler) d.tot.pv = zonKwh + restSolis;
  d.zonBron = zympler ? 'zympler' : d.metingen > 0 ? 'solis' : null;
}

/**
 * De dagtotalen van de balans, of null waar de bron niets leverde.
 *
 * Het verbruik komt uit de energie van Zympler (verbruik_kwh), de delen van de
 * verdeling uit vermogen/4. Voor de verhoudingen maakt dat geen verschil; alleen
 * in het lopende kwartier ligt de energie lager dan vermogen/4.
 */
function totalenVanDeBalans(d: any): void {
  const kwh = (kol: string) => {
    let s = 0;
    for (let i = 0; i < Q; i++) if (d.gemeten.verdeling[i]) s += d.kol[kol][i] / 4;
    return s;
  };
  if (d.heeftVerdeling) for (const k of VERDEEL_TOT) d.tot[k] = kwh(k);
  else for (const k of VERDEEL_TOT) d.tot[k] = null;

  const heeftVerbruik = d.gemeten.verbruik.some((v: number) => v === 1);
  if (heeftVerbruik) {
    let s = 0;
    for (let i = 0; i < Q; i++) if (d.gemeten.verbruik[i]) s += d.kol.load[i] / 4;
    d.tot.load = d._verbruikKwh > 0 ? d._verbruikKwh : s;
  } else {
    d.tot.load = null;
  }
  /* Laden: uit de laadpunten (laad_vermogen, ADR 0039), vermogen/4 over de
     kwartieren met een rij. De ingest schrijft kW = kWh × 4, dus dit is de
     energie van de sessies tot op de wattuur. Zonder laderbron null, geen 0.
     ERE-direct, plan en groen/grijs-einde: geen bron die ze vandaag levert. */
  if (d.gemeten.lader.some((v: number) => v === 1)) {
    let s = 0, s1 = 0, s2 = 0;
    for (let i = 0; i < Q; i++) {
      if (!d.gemeten.lader[i]) continue;
      s += d.kol.chg[i] / 4; s1 += d.kol.chg1[i] / 4; s2 += d.kol.chg2[i] / 4;
    }
    d.tot.chg = s; d.tot.chg1 = s1; d.tot.chg2 = s2;
  } else {
    d.tot.chg = null;
  }
  for (const k of ONBEKEND_ALS_LEEG) d.tot[k] = null;
  d.tot.gaten = null;
}

/* Wijkt het dagtotaal af van de som van de kwartieren, dan klopt er iets niet
   aan de eenheid of aan de aggregatie. Levert een lijst afwijkingen. */
export function controleerDagtotalen(historie: any[], marge = 0.05): any[] {
  const uit: any[] = [];
  for (const d of historie) {
    if (!d.heeftData) continue;
    for (const ov of d.omvormers) {
      const verschil = Math.abs(ov.kwh - ov.kwhBerekend);
      if (verschil > marge) uit.push({ dag: d.sleutel, omvormer: ov.naam, view: ov.kwh, berekend: ov.kwhBerekend, verschil });
    }
  }
  return uit;
}

/* Eén dag als objectrijen per kwartier, voor de klok en de lijn. */
/**
 * De zon per kwartier zoals de dagweergave hem tekent, met wat daarvan geschat is.
 *
 * Gemeten op 17-09-2026: 27% van de omvormerkwartieren kwam niet door van Solis.
 * Een kwartier waarin drie van de vijf omvormers iets meldden telde als drie
 * vijfde van de zon, een kwartier zonder één melding als nul. De grafiek dook
 * midden op de dag naar nul, en dat leest als een wolk.
 *
 * Per kwartier binnen het productievenster:
 *   alle omvormers gemeten → de som, niets geschat;
 *   een deel gemeten       → de som, opgeschaald naar het vermogen van alle
 *                            omvormers (nominaal_kw); het verschil is geschat;
 *   geen enkele            → tussen de buren in, hooguit ZON_MAX_GAT kwartieren;
 *                            langer blijft het null, "geen meting".
 * Buiten het venster is het nacht: 0, en dat is geen schatting.
 *
 * Dit is weergave. kol.pv, de dagtotalen en alles wat de controle leest
 * veranderen niet, en het geschatte deel wordt gearceerd getekend.
 *
 * @param eind  het laatste kwartier dat mee kan doen. Levert de zon op het laatste
 *              gemeten kwartier nog, dan is hij niet onder maar nog niet binnen, en
 *              loopt het venster door tot hier.
 */
export const ZON_MAX_GAT = 4;
export function zonPerKwartier(d: any, eind: number = Q - 1) {
  const zon: (number | null)[] = new Array(Q).fill(0);
  const schat: number[] = new Array(Q).fill(0);
  const status: string[] = new Array(Q).fill('nacht');
  const pvN: number[] = new Array(Q).fill(0);
  const omv = d.omvormers || [];
  if (!omv.length) {
    for (let i = 0; i < Q; i++) { zon[i] = d.kol.pv[i]; status[i] = 'gemeten'; }
    return { zon, schat, status, pvN };
  }
  const gewicht = omv.map((o: any) => (Number(o.nominaalKw) > 0 ? Number(o.nominaalKw) : 1));
  const totaal = gewicht.reduce((a: number, b: number) => a + b, 0);
  const van = d.venster ? d.venster.van : -1;
  let tot = d.venster ? d.venster.tot : -1;
  if (van >= 0 && d.kol.pv[tot] > 0.5) tot = Math.max(tot, Math.min(eind, Q - 1));
  for (let i = 0; i < Q; i++) {
    if (van < 0 || i < van || i > tot) continue;
    let som = 0, w = 0, n = 0;
    omv.forEach((o: any, k: number) => { if (o.heeft[i]) { som += o.kol[i]; w += gewicht[k]; n++; } });
    pvN[i] = n;
    if (n === omv.length) { zon[i] = som; status[i] = 'gemeten'; }
    else if (n > 0) { zon[i] = (som * totaal) / w; schat[i] = (zon[i] as number) - som; status[i] = 'deels'; }
    else { zon[i] = null; status[i] = 'geen'; }
  }
  for (let i = 0; i < Q; i++) {
    if (status[i] !== 'geen') continue;
    let j = i;
    while (j + 1 < Q && status[j + 1] === 'geen') j++;
    const a = i > 0 ? zon[i - 1] : null, b = j + 1 < Q ? zon[j + 1] : null, n = j - i + 1;
    if (n <= ZON_MAX_GAT && a != null && b != null && status[i - 1] !== 'nacht' && status[j + 1] !== 'nacht') {
      for (let k = i; k <= j; k++) {
        const t = (k - i + 1) / (n + 1);
        zon[k] = a + (b - a) * t; schat[k] = zon[k] as number; status[k] = 'tussen';
      }
    }
    i = j;
  }
  return { zon, schat, status, pvN };
}

/**
 * Zet de day-aheadprijzen per kwartier in de dagen van de historie. Een uurprijs
 * vult zijn vier kwartieren. Prijzen buiten de dagen vallen weg.
 *
 * @param prijzen  rijen uit ems.prijs: { tijdstip, duur_min, prijs_eur_kwh }
 */
export function zetPrijzen(historie: any[], prijzen: any[], zone: string): number {
  eisZone(zone);
  const perDag = new Map(historie.map((d) => [d.sleutel, d]));
  let gezet = 0;
  for (const p of prijzen || []) {
    const waarde = Number(p.prijs_eur_kwh);
    const begin = Date.parse(p.tijdstip);
    if (!Number.isFinite(waarde) || !Number.isFinite(begin)) continue;
    const stukken = Number(p.duur_min) === 60 ? 4 : 1;
    for (let k = 0; k < stukken; k++) {
      const { dag, i } = lokaal(new Date(begin + k * 900_000), zone);
      const d = perDag.get(dag);
      if (!d) continue;
      /* Pas een kolom als er een prijs is. NaN is "geen prijs", niet nul: een
         prijs van nul bestaat, en negatief ook. */
      if (!d.kol.prijs) d.kol.prijs = new Float64Array(Q).fill(NaN);
      d.kol.prijs[i] = waarde;
      gezet++;
    }
  }
  return gezet;
}

/**
 * @param opties.vandaag  is dit de dag van nu? Dan is wat na de laatste meting
 *                        komt nog niet gebeurd (toekomst: true) en geen gat.
 */
export function dagRijen(d: any, opties: { vandaag?: boolean } = {}): any[] {
  const laatste = laatstGemeten(d);
  const zon = zonPerKwartier(d, opties.vandaag ? laatste : Q - 1);
  const r: any[] = [];
  for (let i = 0; i < Q; i++) {
    r.push({
      i,
      tijd: `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`,
      toekomst: Boolean(opties.vandaag) && i > laatste,
      /* zon, zonSchat, zonStatus: zie zonPerKwartier. pv blijft de gemeten som. */
      zon: zon.zon[i], zonSchat: zon.schat[i], zonStatus: zon.status[i], pvN: zon.pvN[i], pvTot: (d.omvormers || []).length,
      pv: d.kol.pv[i],
      load: d.kol.load[i], chg: d.kol.chg[i], chg1: d.kol.chg1[i], chg2: d.kol.chg2[i],
      bat: d.kol.bat[i], bat1: d.kol.bat1[i], bat2: d.kol.bat2[i],
      soc: d.kol.soc[i], soc1: d.kol.soc1[i], soc2: d.kol.soc2[i],
      net: d.kol.net[i], netRuw: d.kol.netRuw[i],
      dirCons: d.kol.dirC[i], dirRuim: d.kol.dirR[i], dirGroen: d.kol.dirG[i],
      /* Of er op dit kwartier werkelijk iets gemeten is. Een dag uit de bron
         (greenvision-ems) heeft dit niet; dan is het onbekend en niet vals. */
      heeftBat: d.gemeten ? d.gemeten.bat[i] === 1 : null,
      heeftNet: d.gemeten ? d.gemeten.net[i] === 1 : null,
      heeftSoc: d.gemeten ? d.gemeten.soc[i] === 1 : null,
      /* de balans van de locatie op dit kwartier: het verbruik zoals Zympler het
         mat, en of de verdeling er is */
      heeftVerbruik: d.gemeten?.verbruik ? d.gemeten.verbruik[i] === 1 : null,
      heeftVerdeling: d.gemeten?.verdeling ? d.gemeten.verdeling[i] === 1 : null,
      heeftLader: d.gemeten?.lader ? d.gemeten.lader[i] === 1 : null,
      zonLoad: d.kol.zonLoad[i], zonBat: d.kol.zonBat[i], zonNet: d.kol.zonNet[i], zonChg: d.kol.zonChg[i],
      /* de day-aheadprijs in €/kWh, of null; ook voor wat na nu komt, want die
         prijs staat al vast */
      prijs: d.kol.prijs && Number.isFinite(d.kol.prijs[i]) ? d.kol.prijs[i] : null,
    });
  }
  return r;
}

/**
 * De tijd van de laatste meting van een gegeven op of vóór kwartier idx, of null
 * als er die dag tot dan niets binnenkwam.
 *
 * Voor de kaarten: "geen meting" alleen is een doodlopende melding. "laatst
 * 23:15" zegt of het een haperend kwartier is of een koppeling die stilligt.
 */
export function laatsteMetingTijd(rijen: any[], idx: number, sleutel: 'heeftBat' | 'heeftNet' | 'heeftSoc'): string | null {
  for (let j = Math.min(idx, rijen.length - 1); j >= 0; j--) if (rijen[j]?.[sleutel]) return rijen[j].tijd;
  return null;
}

/**
 * Hoeveel gemeten kwartieren van deze dag het net boven de drempel zat.
 *
 * Niet in tot, zoals in het oude portaal: de drempel is instelbaar op het
 * scherm, en een telling die bij het inlezen vastligt klopt niet meer zodra
 * iemand hem verschuift. tot.boven bleef daardoor altijd nul, en het scherm
 * zei "0 kwartier boven drempel" ongeacht de dag.
 *
 * @returns null zonder drempel — "niet ingevuld" is iets anders dan nul
 */
export function kwartierenBoven(d: any, limiet: number | null | undefined): number | null {
  if (limiet === null || limiet === undefined || !Number.isFinite(limiet)) return null;
  let n = 0;
  for (let i = 0; i < Q; i++) if (d.gemeten?.net?.[i] && d.kol.net[i] > limiet) n++;
  return n;
}

/**
 * Het laatste kwartier van de dag waarop een bron — welke dan ook — iets meldde.
 * -1 als er die dag niets binnenkwam.
 */
export function laatstGemeten(d: any): number {
  const g = d.gemeten || {};
  const maskers = ['bat', 'net', 'soc', 'zon', 'verbruik', 'verdeling'].map((k) => g[k]).filter(Boolean);
  for (let i = Q - 1; i >= 0; i--) {
    if ((d.omvormers || []).some((o: any) => o.heeft[i])) return i;
    if (maskers.some((m: Uint8Array) => m[i] === 1)) return i;
  }
  return -1;
}

/**
 * Het kwartier waarop het scherm opent en dat "Laatste" aanwijst: de nieuwste
 * meting van de dag, van welke bron dan ook.
 *
 * Tot 19-09-2026 was dit het laatste kwartier met zon > 0 waarop alle omvormers
 * meldden. Gingen de omvormers 's avonds uit, dan bleef het scherm om 19:30
 * staan terwijl batterij en net doorliepen tot bijna middernacht. Laatste is
 * gewoon laatste: wat de omvormers op dat kwartier niet meldden, staat op de
 * kaart als nacht of als "loopt achter" (zie omvormerStatus in omvormer.ts).
 *
 * Een dag zonder één meting opent op het midden van de dag.
 */
export function liveKwartier(d: any): number {
  const i = laatstGemeten(d);
  return i >= 0 ? i : 48;
}

/**
 * Per bron het laatste kwartier met een meting, of null. Voor de kop: "zon, batterij
 * en net tot 00:30 · omvormers tot 20:00" zegt meer dan één tijd.
 */
export function laatsteMetingPerBron(d: any): { balans: number | null; bat: number | null; net: number | null; omvormers: number | null } {
  const laatst = (heeft: (i: number) => boolean) => {
    for (let i = Q - 1; i >= 0; i--) if (heeft(i)) return i;
    return null;
  };
  const g = d.gemeten || {};
  return {
    balans: g.zon || g.verbruik ? laatst((i) => g.zon?.[i] === 1 || g.verbruik?.[i] === 1) : null,
    bat: g.bat ? laatst((i) => g.bat[i] === 1) : null,
    net: g.net ? laatst((i) => g.net[i] === 1) : null,
    omvormers: laatst((i) => (d.omvormers || []).some((o: any) => o.heeft[i])),
  };
}

export const kwartierTijd = (i: number) => `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`;

/**
 * Zelfvoorziening: welk deel van het verbruik uit eigen zon kwam.
 *
 *   eigen    = zon naar pand en lader
 *            + batterij naar pand en lader × het groene deel van de lading
 *   verbruik = zon + batterij + net naar pand en lader
 *
 * Alleen over kwartieren waarop de verdeling er is, zodat teller en noemer uit
 * dezelfde kwartieren komen. Het groene deel is groen / (groen + grijs) van de
 * laadstand op dat kwartier; onbekend telt als grijs, zodat 's nachts uit het
 * net geladen stroom nooit als eigen zon meetelt.
 *
 * null als er geen verbruik gemeten is. Tot 19-09-2026 stond hier
 * (pv − export) / max(1, verbruik), en met een verbruik dat onbekend als 0
 * binnenkwam las het scherm "100%, 351 van 0 kWh".
 */
export function zelfvoorziening(dagen: any[]): { deel: number | null; eigen: number; verbruik: number; kwartieren: number } {
  let eigen = 0, verbruik = 0, kwartieren = 0;
  for (const d of dagen) {
    const g = d.gemeten;
    if (!g?.verdeling) continue;
    for (let i = 0; i < Q; i++) {
      if (!g.verdeling[i]) continue;
      const k = d.kol;
      const groen = g.socHerkomst?.[i] && k.socGroen[i] + k.socGrijs[i] > 0
        ? k.socGroen[i] / (k.socGroen[i] + k.socGrijs[i]) : 0;
      eigen += (k.zonLoad[i] + k.zonChg[i] + (k.batLoad[i] + k.batChg[i]) * groen) / 4;
      verbruik += (k.zonV[i] + k.batV[i] + k.netV[i]) / 4;
      kwartieren++;
    }
  }
  return { deel: verbruik > 0 ? Math.min(1, Math.max(0, eigen / verbruik)) : null, eigen, verbruik, kwartieren };
}

/**
 * Hoeveel van de verwachte kwartieren een meting hadden, per dag.
 *
 * Verwacht is: tot en met het laatst gemeten kwartier van vandaag, de hele dag
 * voor een voorbije dag. Een bron telt als hij op die dag überhaupt iets meldde —
 * een locatie zonder batterij mist geen batterijkwartieren. Vervangt tot.gaten,
 * dat nooit gevuld werd en de datakwaliteit daardoor altijd op 100% zette.
 */
export function dekking(dagen: any[], vandaagSleutel: string | null = null): { gemeten: number; verwacht: number } | null {
  let gemeten = 0, verwacht = 0;
  for (const d of dagen) {
    const g = d.gemeten;
    if (!g) continue;
    const bronnen = (['net', 'bat', 'zon', 'verdeling'] as const).filter((k) => g[k]?.some((v: number) => v === 1));
    if (!bronnen.length) continue;
    const eind = d.sleutel === vandaagSleutel ? laatstGemeten(d) : Q - 1;
    for (let i = 0; i <= eind; i++) {
      for (const b of bronnen) { verwacht++; if (g[b][i]) gemeten++; }
    }
  }
  return verwacht ? { gemeten, verwacht } : null;
}
